<script setup>
// This file is used to render a chart in the chart-builder phase (not used in grid mode). BiWidget is used internally in this file.
import { storeToRefs } from 'pinia';
import { BI_ACTUAL_CHARTS } from '~/bi/constants/bi-constants';
import { useBiStore } from '~/bi/store/bi.store';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { sleep } from '~/common/utils/common.utils';

const props = defineProps({
  widgetName: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['continue']);

const { $t } = useCommonImports();

const bi_store = useBiStore();
const { widget_builder_config, widget_builder_data, is_chart_builder_form_valid, widget_builder_metadata } = storeToRefs(bi_store);

const state = reactive({
  last_parsed_config: null,
});

const is_chart_empty = computed(() => {
  return [...BI_ACTUAL_CHARTS, 'number_chart'].includes(widget_builder_config.value.chart.type) && !is_chart_builder_form_valid.value;
});

async function onPublishWidget() {
  let config = {};
  if (widget_builder_config.value.chart.type === 'table') {
    const {
      columns_map = {},
      column_groups = [],
      show_row_headers = false,
      conditional_formatting = [],
    } = widget_builder_config.value.chart;
    config = {
      type: 'table',
      columns_map,
      column_groups,
      show_row_headers,
      conditional_formatting,
    };
  }
  else if (widget_builder_config.value.chart.type === 'pivot_table') {
    const {
      pivot_rows = [],
      pivot_columns = [],
      pivot_values = [],
      show_row_totals = false,
      show_column_totals = false,
      show_grand_totals = false,
      conditional_formatting = [],
    } = widget_builder_config.value.chart;
    config = {
      type: 'pivot_table',
      pivot_rows,
      pivot_columns,
      pivot_values,
      show_row_totals,
      show_column_totals,
      show_grand_totals,
      conditional_formatting,
    };
  }
  else if (['number_chart', 'details_card', 'map'].includes(widget_builder_config.value.chart.type)) {
    config = widget_builder_config.value.chart;
  }
  else if (BI_ACTUAL_CHARTS.includes(widget_builder_config.value.chart.type)) {
    if (!state.last_parsed_config)
      await sleep(300);
    config = state.last_parsed_config;
  }
  emit('continue', config);
}
</script>

<template>
  <div class="h-full w-full flex flex-col relative">
    <div
      class="p-6 flex-1 widget-preview-container"
      :class="{
        'flex justify-center items-center': widget_builder_config.chart.type === 'number_chart',
      }"
    >
      <div v-if="is_chart_empty" class="h-full w-full flex flex-col justify-center items-center text-center">
        <IconIllustrationBiEmptyChartBuilder />
        <div class="text-sm font-semibold text-gray-900 mt-4 mb-1">
          {{ $t('No chart to show') }}
        </div>
        <div class="text-sm font-normal text-gray-600">
          {{ $t('Configure the chart from left panel to start generating the chart') }}
        </div>
      </div>
      <BiWidget
        v-else
        widget-id="preview"
        :config="widget_builder_config"
        :widget-builder-data="widget_builder_data"
        :widget-name="props.widgetName"
        :widget-builder-metadata="widget_builder_metadata"
        @last-parsed-config="state.last_parsed_config = $event"
      />
    </div>

    <hr>
    <div class="p-6 w-full flex justify-end items-center">
      <HawkButton :disabled="is_chart_empty" @click="onPublishWidget">
        <IconHawkRocketTwo />
        {{ $t('Publish widget') }}
      </HawkButton>
    </div>
  </div>
</template>
